import { animated, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';

import { usePopupsContext } from '../../contexts/PopupsContext';

import styles from '../../styles/popups/messages-container.module.css';

type MessagesContainerProps = {
    duration?: number;
    className?: string | null;
};

const MessagesContainer = ({ duration = 5000, className = null }: MessagesContainerProps) => {
    const { messages: originalMessages = null, unregisterMessage } = usePopupsContext();
    const timeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    const messages = (originalMessages || []).slice().reverse();

    const removeMessage = (id: string) => {
        unregisterMessage(id);
        if (typeof timeoutRef.current[id] !== 'undefined') {
            clearTimeout(timeoutRef.current[id]);
        }
        timeoutRef.current = Object.keys(timeoutRef.current)
            .filter((timeoutId) => timeoutId !== id)
            .reduce(
                (subAcc, key) => ({
                    ...subAcc,
                    [key]: timeoutRef.current[key],
                }),
                {},
            );
    };

    useEffect(() => {
        const existingTimeouts = {
            ...timeoutRef.current,
        };
        timeoutRef.current = (messages || []).reduce(
            (acc, { id }) => ({
                ...acc,
                [id]:
                    timeoutRef.current[id] ||
                    setTimeout(() => {
                        removeMessage(id);
                    }, duration),
            }),
            {},
        );
        Object.keys(existingTimeouts).forEach((id) => {
            if (typeof timeoutRef.current[id] === 'undefined') {
                clearTimeout(existingTimeouts[id]);
            }
        });
    }, [messages, duration, removeMessage]);

    const onClickMessage = (id: string) => {
        removeMessage(id);
    };

    useEffect(
        () => () => {
            Object.keys(timeoutRef.current).forEach((id) => {
                clearTimeout(timeoutRef.current[id]);
            });
        },
        [],
    );

    const transitions = useTransition(messages, {
        from: { opacity: 0, y: '-100%', maxHeight: 0 },
        enter: { opacity: 1, y: '0%', maxHeight: 200 },
        leave: { opacity: 0, y: '100%', maxHeight: 200 },
        keys: (item) => item.id,
    });

    return (
        <div className={classNames([styles.container, className])}>
            <div className={styles.messages}>
                {transitions((style, { id, message, theme }) => (
                    <animated.div
                        style={style}
                        className={styles.message}
                        data-theme={theme}
                        onClick={() => onClickMessage(id)}
                    >
                        {message}
                    </animated.div>
                ))}
            </div>
        </div>
    );
};

export default MessagesContainer;
